import { useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ModalWithForm } from '../modal';
import { FormBuilder } from '@/components/formBuilder';
import { useShowToast } from '@/hooks';
import { http } from '@/lib/http';
import { useQuery } from '@tanstack/react-query';
import { projectDetailsApiActions } from '@/store/projectDetails';
import { AlreadyPartOf } from './components/';
import { KText } from '@/ui-library/components';
import { Vector } from '@/assets/icons/tasks';
import { Flex, Icon, Box } from '@chakra-ui/react';
import { CreateProjectDrawer } from '@/pages/projects/components/project-modal/create-project-drawer';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';

export const AddToProject = ({
  modalActions,
  modalClose = () => {},
  contactId,
  isBulk = false,
  onSuccess = () => {},
  alreadyPartOfProps = {},
  bulkOpMethods = null,
  selected = [], // Selected contacts with full data for validation
  // Content-only mode (e.g. job candidates bulk action): render form + validation only, no modal
  asContentOnly = false,
  formRef: externalFormRef = null,
  dispatch: externalDispatch = null,
  toast: externalToast = null,
  submitConfig = null,
  initialValuesConfig = null,
  setIsCreateProjectOpen: externalSetIsCreateProjectOpen = null,
}) => {
  const dispatchFromHook = useDispatch();
  const toastFromHook = useShowToast();
  const dispatch = externalDispatch ?? dispatchFromHook;
  const toast = externalToast ?? toastFromHook;
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);

  const internalBulkOp = useBulkOperation({
    activityType: 'add_to_project',
  });

  const { startTracking, completeSuccess, completeWithErrors, completeFailed } =
    bulkOpMethods || internalBulkOp;

  // Get contact IDs for the action
  const targetIds = useMemo(() => {
    if (asContentOnly || (isBulk && selected?.length > 0)) {
      return selected
        .map((item) => item?.contact?.id || item?.id)
        .filter(Boolean);
    }
    return Array.isArray(contactId) ? contactId : [contactId].filter(Boolean);
  }, [asContentOnly, isBulk, selected, contactId]);

  const {
    data: projectListData,
    isLoading: isProjectsLoading,
    refetch: refetchProjectListData,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => http.get('/projects/list'),
  });

  const setCreatedProjectData = async (data) => {
    await refetchProjectListData();
    if (data?.id) {
      formRef?.current?.setValues({
        ...formRef?.current?.values,
        project_id: data?.id,
      });
    }
  };

  const formConfig = [
    {
      name: 'project_id',
      label: 'Project',
      placeholder: 'Select a Project',
      type: 'select',
      isRequired: true,
      options: projectListData?.data?.projects || [],
      isLoading: isProjectsLoading,
      valueKey: 'id',
      labelKey: 'name',
    },
  ];

  const setCreateProjectOpen =
    externalSetIsCreateProjectOpen ?? setIsCreateProjectOpen;

  const contentOnlyFormConfig = useMemo(
    () =>
      asContentOnly
        ? [
            {
              name: 'projectId',
              label: ' project',
              type: 'select',
              isRequired: true,
              isAppendToBody: true,
              fetchConfig: {
                endPoint: '/projects/list',
                processResponse: (res) => res?.data?.projects,
                httpType: 'v1',
              },
              valueKey: 'id',
              labelKey: 'name',
              containerStyles: {},
            },
            {
              type: 'custom',
              Field: () => (
                <Flex>
                  <KText size="sm-semi-bold">
                    Can't find what you're looking for?{' '}
                  </KText>
                  <KText
                    color="ats-primary"
                    size="sm-semi-bold"
                    onClick={() => setCreateProjectOpen(true)}
                    cursor="pointer"
                    ml="2px"
                  >
                    Create a project
                    <Icon as={Vector} ml="4px" />
                  </KText>
                </Flex>
              ),
            },
          ]
        : [],
    [asContentOnly, setCreateProjectOpen]
  );

  if (asContentOnly) {
    const handleContentOnlySubmit = (values) => {
      const handler = submitConfig?.addToProject || (() => {});
      handler(values, dispatch, toast, bulkOpMethods);
    };

    return (
      <Box>
        <FormBuilder
          initialValues={initialValuesConfig?.({ type: 'addToProject' }) || {}}
          config={contentOnlyFormConfig}
          onSubmit={handleContentOnlySubmit}
          formRef={externalFormRef}
        />
      </Box>
    );
  }

  const handleSubmit = async (values) => {
    setIsLoading(true);

    // Use all selected contacts (validation removed - server will handle duplicates)
    const contactsToAdd = isBulk
      ? targetIds
      : Array.isArray(contactId)
        ? contactId
        : [contactId];
    const totalContacts = contactsToAdd.length;

    // Start tracking in BulkOperationQueue for bulk actions
    if (isBulk) {
      startTracking(totalContacts);
    }

    // Close modal immediately for bulk actions
    if (isBulk) {
      modalClose();
    }

    try {
      const { project_id } = values;
      const urlPaths = { projectId: project_id, add_prospect: 'add_prospect' };
      const requestConfig = {
        contacts: contactsToAdd,
        ...(isBulk && { is_bulk_activity: true }),
      };
      const payload = { urlPaths, requestConfig, isApi: true };
      const result = await dispatch(
        projectDetailsApiActions('addProspectsToProject', payload)
      );

      if (result?.errors?.length) {
        const successCount = result?.data?.length || 0;
        const failedCount = result.errors.length;

        if (isBulk) {
          // Update BulkOperationQueue with errors
          completeWithErrors({
            successCount,
            failedCount,
            errors: result.errors.map((e) => ({
              id: e.id || e.contact_id || 'unknown',
              message: e.message || 'Contact already present in this Project',
            })),
          });
        } else {
          toast({
            title: `Contact addition to project failed - ${result?.errors?.[0]?.message}`,
            status: 'error',
          });
        }
      } else {
        if (isBulk) {
          // Complete successfully in BulkOperationQueue
          completeSuccess(totalContacts);
        } else {
          toast({ title: 'Contact added to project.' });
          modalClose();
        }
        onSuccess();
      }
    } catch {
      if (isBulk) {
        completeFailed('Failed to add contacts to project');
      } else {
        toast({
          title: 'Contact addition to project failed',
          status: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ModalWithForm
        isOpen={modalActions.isOpen}
        onClose={modalClose}
        formConfig={formConfig}
        primaryCtaText="Add"
        customFormRef={formRef}
        isCtaLoading={isLoading}
        headerText="Add candidates to project"
        subHeaderText="Please select a project from the list below"
        handleSubmit={handleSubmit}
        children={
          <Flex>
            <KText size="sm-semi-bold">
              Can't find what you're looking for?{' '}
            </KText>
            <KText
              color={'primary-500'}
              size="sm-semi-bold"
              onClick={() => {
                setIsCreateProjectOpen(true);
              }}
              cursor="pointer"
              ml="2px"
            >
              Create a project
              <Icon as={Vector} ml="4px" />
            </KText>
          </Flex>
        }
        showAdditionalBlockAfter={true}
        AdditionalBlockAfter={AlreadyPartOf}
        additionalBlockAfterProps={alreadyPartOfProps}
      />
      <CreateProjectDrawer
        modalActions={{
          isOpen: isCreateProjectOpen,
          onClose: () => setIsCreateProjectOpen(false),
        }}
        setCreatedProjectData={setCreatedProjectData}
      />
    </>
  );
};
